/** The city is a fixed set of plots you buy one at a time. Start broke — only
 *  the cheapest is affordable. Bigger (pricier) plots = more floors & crew. */
export type BSize = 'small' | 'med' | 'large';

/** Bespoke visual archetype for a plot/building. Each one has its own
 *  hand-drawn SVG sprite in ui-sim/BuildingArt.svelte. */
export type BuildingArt =
  | 'corner'
  | 'stashhouse'
  | 'bodega'
  | 'garage'
  | 'tenement'
  | 'loft'
  | 'motel'
  | 'warehouse'
  | 'factory'
  | 'tower';

export interface CityPlot {
  id: number;
  name: string;
  district: string;
  mapX: number; // 0..100 position on the city map
  mapY: number;
  cost: number;
  size: BSize;
  art: BuildingArt; // which sprite to draw
}

export const SIZE_FLOORS: Record<BSize, { min: number; max: number; crew: number }> = {
  small: { min: -1, max: 1, crew: 4 },
  med: { min: -1, max: 2, crew: 5 },
  large: { min: -2, max: 2, crew: 6 },
};

export const CITY_PLOTS: CityPlot[] = [
  { id: 1, name: 'The Corner', district: 'Eastside', mapX: 18, mapY: 74, cost: 350, size: 'small', art: 'corner' },
  { id: 2, name: 'The Stash House', district: 'The Docks', mapX: 44, mapY: 82, cost: 1500, size: 'small', art: 'stashhouse' },
  { id: 3, name: 'The Bodega', district: 'Old Town', mapX: 72, mapY: 70, cost: 3500, size: 'small', art: 'bodega' },
  { id: 4, name: 'The Garage', district: 'Southgate', mapX: 30, mapY: 54, cost: 7000, size: 'med', art: 'garage' },
  { id: 5, name: 'The Tenement', district: 'Riverside', mapX: 58, mapY: 48, cost: 5000, size: 'small', art: 'tenement' },
  { id: 6, name: 'The Loft', district: 'Midtown', mapX: 82, mapY: 42, cost: 14000, size: 'med', art: 'loft' },
  { id: 7, name: 'The Motel', district: 'The Strip', mapX: 16, mapY: 38, cost: 22000, size: 'med', art: 'motel' },
  { id: 8, name: 'The Warehouse', district: 'The Docks', mapX: 46, mapY: 24, cost: 36000, size: 'large', art: 'warehouse' },
  { id: 9, name: 'The Factory', district: 'Southgate', mapX: 74, mapY: 18, cost: 64000, size: 'large', art: 'factory' },
  { id: 10, name: 'Cloud Nine Tower', district: 'The Heights', mapX: 50, mapY: 8, cost: 140000, size: 'large', art: 'tower' },
];

/** Fallback for buildings created before `art` existed (old saves). */
export function artForName(name: string): BuildingArt {
  const hit = CITY_PLOTS.find((p) => p.name === name);
  if (hit) return hit.art;
  return 'corner';
}

/** Per-district economics — location matters beyond building size. Richer
 *  turf pays more per sale but draws more police attention. */
export interface DistrictMeta {
  demand: number; // sale-price multiplier
  heat: number; // HEAT-generation multiplier
  blurb: string;
}
export const DISTRICT_META: Record<string, DistrictMeta> = {
  Eastside: { demand: 0.85, heat: 0.85, blurb: 'Quiet turf — low demand, low heat.' },
  'The Docks': { demand: 0.95, heat: 0.9, blurb: 'Industrial edge of town — steady and unbothered.' },
  'Old Town': { demand: 1.0, heat: 1.0, blurb: 'Baseline city turf.' },
  Riverside: { demand: 1.0, heat: 1.05, blurb: 'Mixed crowd, a bit more patrol traffic.' },
  Southgate: { demand: 1.1, heat: 1.1, blurb: 'Busy commercial strip — good demand, more eyes.' },
  Midtown: { demand: 1.15, heat: 1.15, blurb: 'Money crowd — pays well, watched well.' },
  'The Strip': { demand: 1.3, heat: 1.25, blurb: 'Nightlife district — big spenders, big heat.' },
  'The Heights': { demand: 1.4, heat: 1.35, blurb: 'Old money — top dollar, top scrutiny.' },
};
export function districtMeta(district: string): DistrictMeta {
  return DISTRICT_META[district] ?? { demand: 1, heat: 1, blurb: '' };
}
